import {useEffect, useRef} from "react";
import {ConfEnv} from "@/config/config";
import {destroyGamanzaWidgets, ensureGamanzaEngage} from "@/services/gamanza/gamanzaEngageSdk";
import {useAuthStore} from "@/store/useAuth.store";
import {useThemeStore} from "@/store/useTheme.store";
import {useIsGamanzaUiEnabled} from "@/utils/gamanzaUi";

/**
 * Keeps Gamanza Engage initialized for authenticated sessions.
 * Theme changes require destroy + re-init (reload cannot change lookAndFeel).
 * Does not mount UI widgets — game sidebar / pages mount their own nodes.
 */
const GamanzaOnSiteHost = () => {
  const authStatus = useAuthStore(state => state.authStatus);
  const token = useAuthStore(state => state.token);
  const theme = useThemeStore(state => state.theme);
  const isEnabled = useIsGamanzaUiEnabled();
  const prevThemeRef = useRef(theme);
  const configReady = Boolean(
    isEnabled && ConfEnv.GAMANZA.CLIENT_ID && ConfEnv.GAMANZA.SERVICE_URL && ConfEnv.GAMANZA.WIDGETS_SCRIPT_URL
  );

  useEffect(() => {
    if (!isEnabled || authStatus !== "authenticated" || !token || !configReady) {
      return undefined;
    }

    let cancelled = false;
    const themeChanged = prevThemeRef.current !== theme;
    prevThemeRef.current = theme;

    void (async () => {
      try {
        // Force full tear-down on theme switch so init() applies new lookAndFeel
        if (themeChanged) {
          await destroyGamanzaWidgets();
        }
        if (cancelled) return;
        await ensureGamanzaEngage(token);
      } catch (_e) {
        // ignore — widgets remount later via hydration hooks
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authStatus, token, configReady, isEnabled, theme]);

  useEffect(() => {
    if (authStatus === "unauthenticated") {
      void destroyGamanzaWidgets();
    }
  }, [authStatus]);

  return null;
};

export default GamanzaOnSiteHost;
